import { FC, ReactNode } from "react";
import { Contract } from "@models/contract.model";
import { formatCurrency } from "src/shared/utils/formatCurrency";
import { UpdateContractPayload } from "@services/finance/contracts/commands/cmdUpdateContract";
import { BILLING_MODEL_LABELS, BILLING_MODEL_OPTIONS, PRODUCT_OPTIONS, BILLING_FREQUENCY_OPTIONS, BILLING_FREQUENCY_LABELS } from "../../types";
import InlineEditField from "../InlineEditField";

const PRODUCT_LABELS: Record<string, string> = Object.fromEntries(
  PRODUCT_OPTIONS.map((p) => [p.value, p.label]),
);

interface Props {
  contract: Contract;
  /** When provided, simple fields become click-to-edit. Omit for read-only. */
  onUpdateContract?: (payload: UpdateContractPayload) => void | Promise<void>;
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="contract-fact">
      <span className="contract-fact__key">{label}</span>
      <span className="contract-fact__val">{children}</span>
    </div>
  );
}

const ContractDetailsSection: FC<Props> = ({ contract, onUpdateContract }) => {
  const editable = !!onUpdateContract;

  const billingLabel = contract.billing_method
    ? BILLING_MODEL_LABELS[contract.billing_method] ?? contract.billing_method
    : null;
  const productsLabel = contract.products.length
    ? contract.products.map((p) => PRODUCT_LABELS[p] ?? p).join(", ")
    : null;

  // Read-only rendering — used on the Engagement page and whenever no handler is passed.
  if (!editable) {
    return (
      <div className="contract-facts">
        <Fact label="Billing method">{billingLabel ?? <em className="muted">—</em>}</Fact>
        {/*
          The subscription's period (MPN-1530 §4.2). Shown for every contract rather than
          only SUBSCRIPTION ones: the Monitor panel gates on these being SET, so leaving
          them visible-but-empty is how someone corrects a contract that was classified
          late. Filling them in on a non-subscription contract is what makes its panel
          appear, which is the honest behaviour -- there is a recurring amount to pace.
      */}
      <Fact label="Billing amount">
        <InlineEditField
          type="currency"
          value={contract.billing_amount != null ? String(contract.billing_amount) : ""}
          placeholder="— set"
          onSave={(v) => save({ billing_amount: v.trim() === "" ? null : Number(v) })}
        />
      </Fact>
      <Fact label="Billing frequency">
        <InlineEditField
          type="select"
          value={contract.billing_frequency ?? ""}
          options={[{ value: "", label: "—" }, ...BILLING_FREQUENCY_OPTIONS]}
          display={
            contract.billing_frequency
              ? BILLING_FREQUENCY_LABELS[contract.billing_frequency] ?? contract.billing_frequency
              : undefined
          }
          placeholder="— set"
          // Empty string, not null: billing_frequency is a non-null CharField
          // defaulting to "" on the server, matching how billing_method is stored.
          onSave={(v) => save({ billing_frequency: v })}
        />
      </Fact>
      <Fact label="PO number">{contract.po ?? <em className="muted">—</em>}</Fact>
        <Fact label="Start date">{contract.contract_start_date ?? <em className="muted">—</em>}</Fact>
        <Fact label="End date">{contract.contract_end_date ?? <em className="muted">—</em>}</Fact>
        <Fact label="Products">{productsLabel ?? <em className="muted">—</em>}</Fact>
        <Fact label="Revenue cap">
          {contract.revenue_cap != null ? formatCurrency(contract.revenue_cap) : <em className="muted">—</em>}
        </Fact>
        <Fact label="Max list size">
          {contract.max_list_size != null ? contract.max_list_size.toLocaleString() : <em className="muted">—</em>}
        </Fact>
        <Fact label="Contract URL">
          {contract.contract_url ? (
            <a href={contract.contract_url} target="_blank" rel="noopener noreferrer">
              <i className="bi bi-box-arrow-up-right me-1" />
              Open
            </a>
          ) : (
            <em className="muted">—</em>
          )}
        </Fact>
      </div>
    );
  }

  const save = onUpdateContract!;

  return (
    <div className="contract-facts">
      <Fact label="Billing method">
        <InlineEditField
          type="select"
          value={contract.billing_method ?? ""}
          options={[{ value: "", label: "—" }, ...BILLING_MODEL_OPTIONS]}
          display={billingLabel ?? undefined}
          placeholder="— set"
          onSave={(v) => save({ billing_method: v || null })}
        />
      </Fact>
      <Fact label="PO number">
        <InlineEditField
          value={contract.po ?? ""}
          onSave={(v) => save({ po: v.trim() || null })}
        />
      </Fact>
      <Fact label="Start date">
        <InlineEditField
          type="date"
          value={contract.contract_start_date ?? ""}
          onSave={(v) => save({ contract_start_date: v || null })}
        />
      </Fact>
      <Fact label="End date">
        <InlineEditField
          type="date"
          value={contract.contract_end_date ?? ""}
          onSave={(v) => save({ contract_end_date: v || null })}
        />
      </Fact>
      <Fact label="Products">{productsLabel ?? <em className="muted">— edit via ✎</em>}</Fact>
      <Fact label="Revenue cap">
        <InlineEditField
          type="currency"
          value={contract.revenue_cap != null ? String(contract.revenue_cap) : ""}
          display={contract.revenue_cap != null ? formatCurrency(contract.revenue_cap) : undefined}
          onSave={(v) => save({ revenue_cap: v.trim() === "" ? null : Number(v) })}
        />
      </Fact>
      {/* Contracted maximum list size: recorded for reference, not enforced (MPN-1950). */}
      <Fact label="Max list size">
        <InlineEditField
          value={contract.max_list_size != null ? String(contract.max_list_size) : ""}
          display={contract.max_list_size != null ? contract.max_list_size.toLocaleString() : undefined}
          placeholder="—"
          onSave={(v) => save({ max_list_size: v.trim() === "" ? null : Number(v.replace(/,/g, "")) })}
        />
      </Fact>
      <Fact label="Contract URL">
        <InlineEditField
          value={contract.contract_url ?? ""}
          display={
            contract.contract_url ? (
              <span>
                <i className="bi bi-box-arrow-up-right me-1" />
                Open
              </span>
            ) : undefined
          }
          onSave={(v) => save({ contract_url: v.trim() || null })}
        />
      </Fact>
    </div>
  );
};

export default ContractDetailsSection;
